// Single key/value cell rendered inside a row-cells group; FK columns show a clickable badge.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { Check, ExternalLink, Pencil, X } from 'lucide-react';
import { resolveCellEditVariant } from '../../common/components/cell-edit/cellEditVariant';
import { SmartCellInput } from '../../common/components/cell-edit/SmartCellInput';
import type { RowCell as RowCellModel } from '../types/singleRowTypes';
import type { LooseRelationship } from '../../../../types/electron';

/**
 * Per-cell smart-edit context. Mirrors `EditableCellContext` / `InlineCellEditorContext`
 * so the same variant decision applies on the Single-Row Details View.
 * Optional — when absent, edit mode renders a plain text input.
 */
export type RowCellSmartEditContext = {
  connectionId: string;
  database: string;
  engine: string;
  /** Source schema where the column physically lives (usually the explored table's schema). */
  schema: string;
  /** Source table where the column physically lives. */
  table: string;
  realFkTarget?: { schema: string; table: string; column: string };
  looseRelCandidates: readonly LooseRelationship[];
  enumDeclared: boolean;
  enumNative: boolean;
  /** Full row used for evaluating each loose-rel's sourceFilter (polymorphic dispatch). */
  sourceRow: Record<string, unknown>;
};

type RowCellProps = {
  readonly cell: RowCellModel;
  readonly density?: 'compact' | 'normal';
  /**
   * Slice 3 (FK Breadcrumb / EP4) — when an FK cell is clicked, the parent
   * SingleRowView pushes a row-node onto the navigation tree. Receives the
   * column + cell value plus the ⌘/Ctrl modifier flag (modifier → open in
   * a new TE tab via openInNewExplorer).
   */
  readonly onFkClick?: (args: {
    column: string;
    value: unknown;
    modifierKey: boolean;
  }) => void;
  /** When true, an Edit (Pencil) affordance appears top-right on hover. */
  readonly editable?: boolean;
  /**
   * Called when the user commits a new value. Carries the column name + the
   * previous and new values so the host can build a quick-edit confirm dialog
   * exactly like the Table Explorer side.
   */
  readonly onEditCommit?: (args: { column: string; oldValue: unknown; newValue: unknown }) => void;
  /** Smart-edit context (FK / enum / boolean variants). Optional. */
  readonly smartEditContext?: RowCellSmartEditContext;
};

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '∅';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function formatForEdit(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function parseEditedValue(text: string, originalValue: unknown): unknown {
  if (text === '') return null;
  if (typeof originalValue === 'number') {
    const n = Number(text);
    if (!Number.isNaN(n)) return n;
  }
  if (typeof originalValue === 'boolean') {
    const lower = text.toLowerCase();
    if (lower === 'true' || lower === '1') return true;
    if (lower === 'false' || lower === '0') return false;
  }
  return text;
}

export function RowCell({
  cell,
  density = 'normal',
  onFkClick,
  editable = false,
  onEditCommit,
  smartEditContext,
}: RowCellProps) {
  const display = formatValue(cell.value);
  const isNull = cell.value === null || cell.value === undefined;
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(() => formatForEdit(cell.value));
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset editing buffer when the cell value changes from outside.
  useEffect(() => {
    if (!isEditing) setEditText(formatForEdit(cell.value));
  }, [cell.value, isEditing]);

  // Focus + select when entering edit mode (plain branch).
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const smartVariant = useMemo(() => {
    if (!smartEditContext) return undefined;
    return resolveCellEditVariant({
      columnId: cell.column.name,
      dataType: cell.column.dataType,
      isRealForeignKey: !!smartEditContext.realFkTarget,
      realFkTarget: smartEditContext.realFkTarget,
      declaredEnumColumnIds: smartEditContext.enumDeclared ? new Set([cell.column.name]) : new Set(),
      nativeEnumColumnIds: smartEditContext.enumNative ? new Set([cell.column.name]) : new Set(),
      looseRelCandidates: smartEditContext.looseRelCandidates,
      sourceRow: smartEditContext.sourceRow,
      nullable: cell.column.nullable,
    });
  }, [smartEditContext, cell.column.name, cell.column.dataType, cell.column.nullable]);

  const handleStartEdit = useCallback(() => {
    setEditText(formatForEdit(cell.value));
    setIsEditing(true);
  }, [cell.value]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditText(formatForEdit(cell.value));
  }, [cell.value]);

  const handleCommitFromPlain = useCallback(() => {
    const parsed = parseEditedValue(editText, cell.value);
    const sameAsOriginal = parsed === cell.value || String(parsed) === String(cell.value);
    setIsEditing(false);
    if (sameAsOriginal) return;
    onEditCommit?.({ column: cell.column.name, oldValue: cell.value, newValue: parsed });
  }, [editText, cell.value, cell.column.name, onEditCommit]);

  const handleCommitFromSmart = useCallback(
    (newValue: unknown) => {
      setIsEditing(false);
      onEditCommit?.({ column: cell.column.name, oldValue: cell.value, newValue });
    },
    [cell.value, cell.column.name, onEditCommit],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Ctrl/Cmd+0 → commit NULL on any nullable cell. preventDefault to
      // stop the browser's zoom-reset shortcut while a cell editor is focused.
      if ((e.ctrlKey || e.metaKey) && e.key === '0') {
        if (cell.column.nullable === false) return;
        e.preventDefault();
        setIsEditing(false);
        if (cell.value === null || cell.value === undefined) return;
        onEditCommit?.({ column: cell.column.name, oldValue: cell.value, newValue: null });
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCommitFromPlain();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleCancelEdit();
      }
    },
    [handleCommitFromPlain, handleCancelEdit, cell.column.nullable, cell.column.name, cell.value, onEditCommit],
  );

  const useSmartEditor =
    isEditing && smartEditContext && smartVariant && smartVariant.variant !== 'plain';

  // Compact density renders as a single horizontal row (label + value on the
  // same line, smaller padding). Normal renders as a 2-line stacked card.
  if (density === 'compact') {
    return (
      <div
        className="flex items-center gap-2 border-b border-divider px-1.5 py-0.5 min-w-0 text-[11px] group/cell relative"
        data-testid="row-cell"
        data-column={cell.column.name}
      >
        <span className="text-[10px] text-muted-foreground truncate flex-shrink-0 max-w-[40%]">
          {cell.column.name}
        </span>
        {cell.isForeignKey && (
          <span
            className="inline-flex items-center px-1 text-[9px] rounded border border-blue-500/30 bg-blue-500/10 text-blue-500 flex-shrink-0"
            title="Foreign key"
            data-testid="row-cell-fk-badge"
          >
            FK
          </span>
        )}
        {isEditing ? (
          useSmartEditor ? (
            <div className="flex-1 min-w-0">
              <SmartCellInput
                variant={smartVariant!}
                initialValue={cell.value}
                connectionId={smartEditContext!.connectionId}
                database={smartEditContext!.database}
                engine={smartEditContext!.engine}
                schema={smartEditContext!.schema}
                table={smartEditContext!.table}
                column={cell.column.name}
                dataType={cell.column.dataType}
                enumSource={smartEditContext!.enumDeclared ? 'declared' : 'native'}
                onCommit={handleCommitFromSmart}
                onCancel={handleCancelEdit}
              />
            </div>
          ) : (
            <div className="flex items-stretch gap-1 flex-1 min-w-0">
              <input
                ref={inputRef}
                type="text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 min-w-0 h-5 px-1 text-[11px] bg-background border border-primary rounded-sm outline-none"
                data-testid="row-cell-edit-input"
              />
              <button
                type="button"
                className="h-5 w-5 inline-flex items-center justify-center rounded-sm text-emerald-600 hover:bg-emerald-500/15 hover:text-emerald-700"
                onClick={handleCommitFromPlain}
                title="Confirm"
                aria-label="Confirm edit"
                data-testid="row-cell-edit-confirm-btn"
              >
                <Check className="h-3 w-3" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="h-5 w-5 inline-flex items-center justify-center rounded-sm text-red-600 hover:bg-red-500/15 hover:text-red-700"
                onClick={handleCancelEdit}
                title="Cancel"
                aria-label="Cancel edit"
                data-testid="row-cell-edit-cancel-btn"
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </div>
          )
        ) : (
          <span
            className={`truncate min-w-0 flex-1 ${isNull ? 'text-muted-foreground italic' : 'text-foreground'}`}
            title={isNull ? 'null' : display}
          >
            {isNull ? 'null' : display}
          </span>
        )}
        {editable && !isEditing && (
          <button
            type="button"
            className="shrink-0 inline-flex h-4 w-4 items-center justify-center rounded-sm text-muted-foreground opacity-0 group-hover/cell:opacity-100 focus:opacity-100 hover:text-foreground hover:bg-accent"
            onClick={handleStartEdit}
            title="Edit value"
            aria-label="Edit value"
            data-testid="row-cell-edit-btn"
          >
            <Pencil className="h-3 w-3" aria-hidden="true" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-0.5 border-b border-divider px-2 py-1.5 min-w-0 group/cell relative"
      data-testid="row-cell"
      data-column={cell.column.name}
    >
      <div className="flex items-center gap-1 min-w-0">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
          {cell.column.name}
        </span>
        {cell.isForeignKey && (
          <span
            className="inline-flex items-center px-1 text-[9px] rounded border border-blue-500/30 bg-blue-500/10 text-blue-500"
            title="Foreign key"
            data-testid="row-cell-fk-badge"
          >
            FK
          </span>
        )}
        {editable && !isEditing && (
          <button
            type="button"
            className="ml-auto inline-flex h-4 w-4 items-center justify-center rounded-sm text-muted-foreground opacity-0 group-hover/cell:opacity-100 focus:opacity-100 hover:text-foreground hover:bg-accent"
            onClick={handleStartEdit}
            title="Edit value"
            aria-label="Edit value"
            data-testid="row-cell-edit-btn"
          >
            <Pencil className="h-3 w-3" aria-hidden="true" />
          </button>
        )}
      </div>
      <div
        className={`text-xs truncate min-w-0 ${isNull ? 'text-muted-foreground italic' : 'text-foreground'}`}
        title={isNull ? 'null' : display}
      >
        {isEditing ? (
          useSmartEditor ? (
            <SmartCellInput
              variant={smartVariant!}
              initialValue={cell.value}
              connectionId={smartEditContext!.connectionId}
              database={smartEditContext!.database}
              engine={smartEditContext!.engine}
              schema={smartEditContext!.schema}
              table={smartEditContext!.table}
              column={cell.column.name}
              dataType={cell.column.dataType}
              enumSource={smartEditContext!.enumDeclared ? 'declared' : 'native'}
              onCommit={handleCommitFromSmart}
              onCancel={handleCancelEdit}
            />
          ) : (
            <div className="flex items-stretch gap-1">
              <input
                ref={inputRef}
                type="text"
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={handleKeyDown}
                className="flex-1 min-w-0 h-6 px-1 text-xs bg-background border border-primary rounded-sm outline-none"
                data-testid="row-cell-edit-input"
              />
              <button
                type="button"
                className="h-6 w-6 inline-flex items-center justify-center rounded-sm text-emerald-600 hover:bg-emerald-500/15 hover:text-emerald-700"
                onClick={handleCommitFromPlain}
                title="Confirm"
                aria-label="Confirm edit"
                data-testid="row-cell-edit-confirm-btn"
              >
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="h-6 w-6 inline-flex items-center justify-center rounded-sm text-red-600 hover:bg-red-500/15 hover:text-red-700"
                onClick={handleCancelEdit}
                title="Cancel"
                aria-label="Cancel edit"
                data-testid="row-cell-edit-cancel-btn"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          )
        ) : cell.isForeignKey && !isNull ? (
          // Slice 3 (EP4) — FK cell click inside the Single-Row View row-details
          // pushes a row-node directly (no popover). ⌘/Ctrl-click opens in a
          // new TE tab via the parent's `onFkClick` handler.
          <button
            type="button"
            className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:underline truncate min-w-0"
            data-testid="row-cell-fk-link"
            onClick={(e: ReactMouseEvent) => {
              const modifierKey = e.metaKey || e.ctrlKey;
              onFkClick?.({ column: cell.column.name, value: cell.value, modifierKey });
            }}
            title="Open referenced record"
          >
            <span className="truncate min-w-0">{display}</span>
            <ExternalLink className="h-3 w-3 flex-shrink-0" aria-hidden="true" />
          </button>
        ) : (
          <span>{isNull ? 'null' : display}</span>
        )}
      </div>
    </div>
  );
}
